import { ComponentType, HTMLAttributes } from 'react';
import { BoxHTMLProps, BoxOptions, VisuallyHidden } from 'reakit';
import { ArrowForward } from '@mos-cat/ds-icons';
import { createHook } from 'reakit-system';
import type { DisplayProps } from 'styled-system';
import { SystemStyleObject } from '@styled-system/css';
import { Box, BoxProps, useBox } from '../Box/Box';
import { focus } from '../utils/focusable';
import { StylesOptions } from '../hooks/useStyles';

import { useRequiredPropWarning } from '../hooks/useRequiredPropWarning';
import pxToRem from '../utils/pxToRem';
import { WHCM, WHCMstyles } from '../constants';
import { getNumbers } from './helpers';

type CustomLinkProps = HTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

type Props = {
  total: number;
  current: number;
  getHref: (page: number) => string;
  linkComponent?: ComponentType<CustomLinkProps>;
  linkProps?: HTMLAttributes<HTMLAnchorElement>;
} & BoxProps;

type CommonItemProps = {
  href: string;
  linkProps?: HTMLAttributes<HTMLAnchorElement>;
  linkComponent?: ComponentType<CustomLinkProps>;
};

type PageItemProps = CommonItemProps & {
  children: number;
  isActive: boolean;
} & Required<DisplayProps>;

type ToStartItemProps = CommonItemProps;
type NextItemProps = CommonItemProps;

const itemStyles = {
  height: pxToRem(40),
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'pageBg',
  borderWidth: 1,
  borderColor: 'pageBg',
  borderRadius: 'paginationBorderRadius',
  color: 'pageColor',
  px: 'sp-4',
  py: 'sp-8',
  fontSize: 'paginationFontSize',
  lineHeight: 'paginationLineHeight',

  '&:hover': {
    backgroundColor: 'pageHoverBg',
    borderColor: 'pageHoverBg',
  },
  '&:active': {
    backgroundColor: 'pageActiveBg',
    borderColor: 'pageActiveBg',
  },
  '&:focus': focus,

  '&[aria-current]': {
    backgroundColor: 'pageCurrentBg',
    borderColor: 'pageCurrentBg',

    '&:hover': {
      backgroundColor: 'pageCurrentHoverBg',
      borderColor: 'pageCurrentHoverBg',
    },
    '&:active': {
      backgroundColor: 'pageCurrentActiveBg',
      borderColor: 'pageCurrentActiveBg',
    },
  },

  [WHCM]: {
    ...WHCMstyles,
    color: 'WindowText',

    '&[aria-current]': {
      color: 'ActiveBorder',
      borderWidth: 2,
    },
  },
};

const useItem = createHook<
  BoxOptions & StylesOptions & Partial<Pick<PageItemProps, 'isActive'>>,
  BoxHTMLProps
>({
  name: 'PaginationItem',
  keys: ['isActive'],
  compose: [useBox],
  useOptions(options) {
    return {
      ...options,
      styles: {
        ...itemStyles,
        ...options.styles,
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      'aria-current': options.isActive ? 'true' : undefined,
    };
  },
});

function PageItem({
  children,
  href,
  isActive,
  linkProps,
  linkComponent,
  display,
}: PageItemProps) {
  const itemProps = useItem({
    isActive,
    styles: {
      display,
      width: pxToRem(40),
      mr: 'sp-4',
    } as SystemStyleObject,
  });
  const Item = linkComponent ?? 'a';
  return (
    <Box as="li" mt="sp-0">
      <Item href={href} {...itemProps} {...linkProps}>
        <VisuallyHidden>Страница </VisuallyHidden>
        {children}
      </Item>
    </Box>
  );
}

function ToStartItem({ href, linkProps, linkComponent }: ToStartItemProps) {
  const itemProps = useItem(
    {
      styles: {
        minWidth: pxToRem(40),
        px: ['sp-4', 'sp-12'],
        display: 'flex',
        borderRadius: 'paginationBorderRadius',
        backgroundColor: 'white',
        mr: 'sp-4',
      },
    },
    linkProps
  );
  const Item = linkComponent ?? 'a';
  return (
    <>
      <Box as="li" mt="sp-0">
        <Item href={href} {...itemProps}>
          <Box as="span" display={['inline', 'none']}>
            <VisuallyHidden>Перейти к странице </VisuallyHidden> 1
          </Box>
          <Box as="span" display={['none', 'inline']}>
            В&nbsp;начало
          </Box>
        </Item>
      </Box>
      <Box
        as="li"
        aria-hidden
        display={['flex', 'none']}
        tabIndex={-1}
        width={pxToRem(40)}
        alignItems="center"
        justifyContent="center"
        mr="sp-4"
        my="sp-0"
      >
        ...
      </Box>
    </>
  );
}

function NextItem({ href, linkProps, linkComponent }: NextItemProps) {
  const Item = linkComponent ?? 'a';
  const itemProps = useItem(
    {
      styles: {
        px: 'sp-12',
        py: 'sp-8',
        borderRadius: 'paginationBorderRadius',
        display: 'flex',
        minWidth: pxToRem(64),
      },
    },
    linkProps
  );
  return (
    <Box
      as="li"
      mt="sp-0"
      flexGrow={[1, 0]}
      display="flex"
      justifyContent="flex-end"
    >
      <Item href={href} {...itemProps}>
        <Box as="span" display={['block', 'none']}>
          <ArrowForward />
          <VisuallyHidden>Следующая страница</VisuallyHidden>
        </Box>
        <Box as="span" display={['none', 'block']}>
          Следующая <VisuallyHidden>страница</VisuallyHidden>
        </Box>
      </Item>
    </Box>
  );
}

export function Pagination({
  total,
  current,
  getHref,
  linkProps,
  linkComponent,
  ...other
}: Props) {
  useRequiredPropWarning(Boolean(total), 'Pagination', 'total');
  useRequiredPropWarning(
    Boolean(current) || current === 0,
    'Pagination',
    'current'
  );
  useRequiredPropWarning(Boolean(getHref), 'Pagination', 'getHref');
  const showToStart = current >= 4 && total > 5;
  const showNext = current < total && total > 5;
  const pageNumbers = getNumbers(current, total);
  const pageNumbersElements = pageNumbers.map(({ value, display }) => (
    <PageItem
      display={display}
      key={value}
      isActive={value === current}
      linkProps={linkProps}
      href={getHref(value)}
      linkComponent={linkComponent}
    >
      {value}
    </PageItem>
  ));

  return (
    <Box
      as="nav"
      {...other}
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- нужна проверка на пустое значение
      aria-label={other['aria-label'] || 'Навигация по страницам'}
    >
      <Box
        as="ul"
        display="flex"
        alignItems="center"
        styles={{ listStyleType: 'none' }}
        padding="sp-0"
        margin="sp-0"
      >
        {showToStart && (
          <ToStartItem
            linkComponent={linkComponent}
            linkProps={linkProps}
            href={getHref(1)}
          />
        )}
        {pageNumbersElements}
        {showNext && (
          <NextItem
            linkComponent={linkComponent}
            linkProps={linkProps}
            href={getHref(current + 1)}
          />
        )}
      </Box>
    </Box>
  );
}

export type PaginationProps = Props;
